/**
 * CPUPlayer — AI that plays the game automatically.
 *
 * Each WORKSHOP phase:
 * 1. Preview the next wave's enemy composition
 * 2. Pick templates that counter the enemy mix (anti-air if flyers, DPS if tanks, etc.)
 * 3. Place robots on valid tiles near paths
 * 4. Start the wave
 *
 * Activated via GOD.cpuBot toggle.
 */

import { TILE_SIZE, MAP_COLS, MAP_ROWS, FLYING_ENEMY_TYPES, TileType, EnemyType } from '@/config/GameConfig';
import { ComponentType, COMPONENT_DEFS, WeaponTarget } from '@/components/ComponentDefs';
import { GOD } from '@/config/GodMode';
import { MAP_GRID, SPAWN_POSITIONS, BASE_POSITIONS } from '@/map/MapData';
import { Robot } from '@/entities/Robot';
import { BehaviorRule } from '@/behaviors/BehaviorDefs';
import { EventBus, GameEvents } from '@/events/EventBus';
import { WaveEntry } from '@/systems/WaveComposer';
import { RobotTemplate, getTemplates } from '@/config/RobotTemplates';

// ── Template definition (mirrors WorkshopScene.TEMPLATES structure) ──
interface Template {
  name: string;
  label: string;
  components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[];
  rules: BehaviorRule[];
}

// ── Placement candidate ──
interface PlacementTile {
  col: number;
  row: number;
  pathDist: number; // distance to nearest path tile (lower = better coverage)
}

// ── Template score tags ──
interface ScoredTemplate {
  template: Template;
  cost: number;
  canTargetAir: boolean;
  canTargetGround: boolean;
  isFlying: boolean;
  isMobile: boolean;
  isSupport: boolean;
  hasMines: boolean;
  dps: number; // rough damage estimate
}

export class CPUPlayer {
  private scene: Phaser.Scene;
  private active = false;
  private delayTimer: Phaser.Time.TimerEvent | null = null;
  /** True after the bot has handled wave 1 (prevents double-trigger) */
  private firstTurnDone = false;

  /** Tiles occupied by CPU-placed robots this round */
  private occupiedTiles = new Set<string>();
  /** Precomputed Manhattan distance to nearest PATH tile (built once per turn via BFS) */
  private pathDistField: number[][] | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Call once after GameScene.create() — hooks into phase events */
  init(): void {
    // Wave 1: react to initial WORKSHOP phase to auto-place + start
    EventBus.on(GameEvents.PHASE_CHANGED, this.onPhaseChanged, this);
    // Clear occupied tiles when wave ends
    EventBus.on(GameEvents.WAVE_COMPLETED, this.onWaveCompleted, this);
    // Waves 2+: act when the wave-clear overlay is ready
    EventBus.on(GameEvents.WAVE_TRANSITION_READY, this.onTransitionReady, this);
  }

  /** Handles initial WORKSHOP phase — bot auto-plays wave 1 */
  private onPhaseChanged = (phase: string): void => {
    if (phase !== 'workshop') return;
    if (!GOD.cpuBot) return;
    if (this.firstTurnDone) return;

    this.firstTurnDone = true;
    this.active = true;
    // Short delay so UIScene is fully ready
    this.delayTimer = this.scene.time.delayedCall(1500, () => {
      this.playTurn();
      this.active = false;
    });
  };

  private onWaveCompleted = (): void => {
    this.occupiedTiles.clear();
  };

  /** Fires after repair sequence — the wave-clear overlay is now showing (waves 2+) */
  private onTransitionReady = (): void => {
    if (!GOD.cpuBot) return;
    if (this.active) return;

    // Wait before auto-playing — gives time to see results
    this.active = true;
    this.delayTimer = this.scene.time.delayedCall(2000, () => {
      this.playTurn();
      this.active = false;
    });
  };

  // ══════════════════════════════════════════
  //  MAIN LOGIC
  // ══════════════════════════════════════════

  private playTurn(): void {
    const gameScene = this.scene.registry.get('gameScene') as import('@/scenes/GameScene').GameScene;
    const waveManager = this.scene.registry.get('waveManager') as import('@/systems/WaveManager').WaveManager;
    const robotManager = this.scene.registry.get('robotManager') as import('@/systems/RobotManager').RobotManager;
    if (!gameScene || !waveManager || !robotManager) return;

    // 0. Precompute path distance field (BFS, O(rows×cols))
    this.buildPathDistField();

    // 1. Get available templates
    const templates: Template[] = [...getTemplates()] as Template[];

    // 2. Preview next wave enemies
    const waveEntries: WaveEntry[] = waveManager.getNextWaveEntries();
    const flyerRatio = this.getFlyerRatio(waveEntries);
    const hasTanks = waveEntries.some(e => e.type === EnemyType.TANK || e.type === EnemyType.BRUTE);

    // 3. Score templates
    const scored = templates.map(t => this.scoreTemplate(t));

    // 4. Evaluate current army — what do we already have?
    const existingRobots = robotManager.robots.filter(r => r.alive);
    let existingAntiAir = 0;
    let existingDPS = 0;
    for (const r of existingRobots) {
      for (const c of r.components) {
        if (c.broken) continue;
        const wt = c.def.weaponTarget;
        if (wt === WeaponTarget.AIR || wt === WeaponTarget.BOTH) existingAntiAir++;
        if (c.def.damage > 0) existingDPS += c.def.damage;
      }
      // Mark their tiles as occupied
      const col = Math.floor(r.x / TILE_SIZE);
      const row = Math.floor(r.y / TILE_SIZE);
      this.occupiedTiles.add(`${col}_${row}`);
    }

    // 5. Build placement candidates
    const fixedTiles = this.getPlacementTiles('fixed');
    const mobileTiles = this.getPlacementTiles('mobile');
    const flyingTiles = this.getPlacementTiles('flying');

    // 6. Decide how many robots to place (budget-limited)
    let budget = gameScene.resources;
    const picks: { scored: ScoredTemplate; tiles: PlacementTile[] }[] = [];

    // Priority: if flyers incoming and no anti-air, force anti-air first
    const needAntiAir = flyerRatio > 0.1 && existingAntiAir < 2;
    if (needAntiAir) {
      const antiAirTemplates = scored.filter(s => s.canTargetAir);
      if (antiAirTemplates.length > 0) {
        // Pick best affordable anti-air
        const pick = this.pickBest(antiAirTemplates, budget, flyerRatio, hasTanks);
        if (pick) {
          picks.push({ scored: pick, tiles: pick.isFlying ? flyingTiles : (pick.isMobile ? mobileTiles : fixedTiles) });
          budget -= GOD.freeBuilding ? 0 : pick.cost;
        }
      }
    }

    // Fill remaining budget with a mix
    const maxRobots = 5; // don't spam too many per round
    let placed = picks.length;
    while (budget > 0 && placed < maxRobots) {
      const affordable = scored.filter(s => (GOD.freeBuilding ? 0 : s.cost) <= budget);
      if (affordable.length === 0) break;

      const pick = this.pickBest(affordable, budget, flyerRatio, hasTanks);
      if (!pick) break;

      const tiles = pick.isFlying ? flyingTiles : (pick.isMobile ? mobileTiles : fixedTiles);
      if (tiles.length === 0) break;

      picks.push({ scored: pick, tiles });
      budget -= GOD.freeBuilding ? 0 : pick.cost;
      placed++;
    }

    // 7. Place the robots
    for (const { scored: s, tiles } of picks) {
      const tile = this.pickTile(tiles);
      if (!tile) continue;

      const rx = tile.col * TILE_SIZE + TILE_SIZE / 2;
      const ry = tile.row * TILE_SIZE + TILE_SIZE / 2;
      const robot = new Robot(this.scene, rx, ry);
      for (const comp of s.template.components) {
        robot.addComponent(comp.type, comp.lx, comp.ly, comp.rotation ?? 0, comp.flipX ?? false, comp.flipY ?? false, comp.evoLevel ?? 0);
      }
      // Set heading BEFORE resetHpEnergy so stability uses correct flight axis
      const dirComp = s.template.components.find(c => c.type === ComponentType.DIRECTION);
      if (dirComp) {
        const rot = dirComp.rotation ?? 0;
        robot.heading = rot;
        robot.buildHeading = rot;
        robot.recalcStats();
      }
      robot.resetHpEnergy();
      robot.normalizeSize();
      robot.placed = true;

      robotManager.addRobot(robot, [...s.template.rules]);
      const cost = GOD.freeBuilding ? 0 : s.cost;
      gameScene.resources -= cost;
      EventBus.emit(GameEvents.RESOURCES_CHANGED, gameScene.resources);
      this.occupiedTiles.add(`${tile.col}_${tile.row}`);
    }

    // 8. Only request wave start if there are alive robots on the field
    const aliveCount = robotManager.robots.filter(r => r.alive).length;
    if (aliveCount === 0) {
      // Nothing placed and no existing robots — retry after a delay
      this.delayTimer = this.scene.time.delayedCall(2000, () => {
        this.active = true;
        this.playTurn();
        this.active = false;
      });
      return;
    }

    // Request wave start through the UI flow (dismisses overlay + starts wave properly)
    this.scene.time.delayedCall(400, () => {
      EventBus.emit(GameEvents.CPU_REQUEST_WAVE);
    });
  }

  // ══════════════════════════════════════════
  //  TEMPLATE ANALYSIS
  // ══════════════════════════════════════════

  private scoreTemplate(t: Template): ScoredTemplate {
    let cost = 0;
    let canTargetAir = false;
    let canTargetGround = false;
    let isFlying = false;
    let isMobile = false;
    let isSupport = false;
    let hasMines = false;
    let dps = 0;

    for (const comp of t.components) {
      const def = COMPONENT_DEFS[comp.type];
      cost += def.cost;

      if (def.weaponTarget === WeaponTarget.AIR || def.weaponTarget === WeaponTarget.BOTH) canTargetAir = true;
      if (def.weaponTarget === WeaponTarget.GROUND || def.weaponTarget === WeaponTarget.BOTH) canTargetGround = true;
      if (comp.type === ComponentType.WING || comp.type === ComponentType.TURBINE) isFlying = true;
      if (comp.type === ComponentType.WHEEL) isMobile = true;
      if (comp.type === ComponentType.REPAIR_MODULE || comp.type === ComponentType.RECHARGE_MODULE) isSupport = true;
      if (comp.type === ComponentType.MINE_LAYER) hasMines = true;
      if (def.damage > 0 && def.fireRate > 0) dps += def.damage * def.fireRate;
    }

    return { template: t, cost, canTargetAir, canTargetGround, isFlying, isMobile, isSupport, hasMines, dps };
  }

  private pickBest(
    candidates: ScoredTemplate[], budget: number,
    flyerRatio: number, hasTanks: boolean,
  ): ScoredTemplate | null {
    if (candidates.length === 0) return null;

    // Score each candidate based on situation
    const withScores = candidates.map(s => {
      let score = s.dps * 0.5; // base: raw DPS

      // Anti-air bonus when flyers incoming
      if (s.canTargetAir && flyerRatio > 0) score += 40 * flyerRatio;
      // Heavy hitter bonus when tanks incoming
      if (hasTanks && s.dps > 20) score += 15;
      // Mine layer bonus for area denial
      if (s.hasMines) score += 10;
      // Support penalty (don't over-build support)
      if (s.isSupport) score -= 5;
      // Cost efficiency: prefer cheaper templates when budget is tight
      const effectiveCost = GOD.freeBuilding ? 1 : Math.max(1, s.cost);
      score += (budget / effectiveCost) * 2;
      // Small random to avoid always picking the same
      score += Math.random() * 8;

      return { scored: s, score };
    });

    withScores.sort((a, b) => b.score - a.score);
    return withScores[0].scored;
  }

  // ══════════════════════════════════════════
  //  WAVE ANALYSIS
  // ══════════════════════════════════════════

  private getFlyerRatio(entries: WaveEntry[]): number {
    if (entries.length === 0) return 0;
    const flyers = entries.filter(e => FLYING_ENEMY_TYPES.has(e.type)).length;
    return flyers / entries.length;
  }

  // ══════════════════════════════════════════
  //  PLACEMENT
  // ══════════════════════════════════════════

  private getPlacementTiles(kind: 'fixed' | 'mobile' | 'flying'): PlacementTile[] {
    const robotManager = this.scene.registry.get('robotManager') as import('@/systems/RobotManager').RobotManager;
    // Collect alive fixed robots as anchors (moveForce === 0, not flying)
    const anchors = robotManager?.robots.filter(r => r.alive && r.moveForce === 0 && !r.canFly()) ?? [];

    const tiles: PlacementTile[] = [];
    for (let row = 0; row < MAP_ROWS; row++) {
      for (let col = 0; col < MAP_COLS; col++) {
        if (this.occupiedTiles.has(`${col}_${row}`)) continue;
        const t = MAP_GRID[row]?.[col];
        if (t === undefined) continue;

        let valid = false;
        if (kind === 'fixed') {
          valid = t === TileType.PLACEMENT || t === TileType.GRILLE || t === TileType.BROKEN_PLACEMENT;
        } else if (kind === 'mobile') {
          valid = t === TileType.PLACEMENT || t === TileType.GRILLE || t === TileType.BROKEN_PLACEMENT ||
                  t === TileType.PATH || t === TileType.MUD || t === TileType.GROUND;
        } else {
          valid = t !== TileType.WALL && t !== TileType.WATER;
        }
        if (!valid) continue;

        // Anchor rule: mobile/flying must be within sensor range of a fixed robot OR within base deploy range
        if (kind !== 'fixed') {
          const cx = col * TILE_SIZE + TILE_SIZE / 2;
          const cy = row * TILE_SIZE + TILE_SIZE / 2;
          const nearRobotAnchor = anchors.some(r => {
            const dx = r.x - cx, dy = r.y - cy;
            return dx * dx + dy * dy <= r.sensorRange * r.sensorRange;
          });
          const baseRange = GOD.baseDeployRange * TILE_SIZE;
          const baseRangeSq = baseRange * baseRange;
          const nearBase = baseRange > 0 && BASE_POSITIONS.some(bp => {
            const dx = bp.x - cx, dy = bp.y - cy;
            return dx * dx + dy * dy <= baseRangeSq;
          });
          if (!nearRobotAnchor && !nearBase) continue;
        }

        // Compute distance to nearest path tile
        const pathDist = this.distToPath(col, row);
        tiles.push({ col, row, pathDist });
      }
    }
    // Sort by proximity to path (strategic positioning)
    tiles.sort((a, b) => a.pathDist - b.pathDist);
    return tiles;
  }

  /** Build BFS distance field from all PATH tiles (O(rows×cols), called once per turn) */
  private buildPathDistField(): void {
    const field: number[][] = [];
    const queue: [number, number][] = [];
    for (let r = 0; r < MAP_ROWS; r++) {
      field[r] = new Array(MAP_COLS).fill(Infinity);
      for (let c = 0; c < MAP_COLS; c++) {
        if (MAP_GRID[r][c] === TileType.PATH) {
          field[r][c] = 0;
          queue.push([r, c]);
        }
      }
    }
    // BFS (Manhattan neighbors) — floods outward from all PATH tiles simultaneously
    let head = 0;
    while (head < queue.length) {
      const [r, c] = queue[head++];
      const nd = field[r][c] + 1;
      for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
        const nr = r + dr, nc = c + dc;
        if (nr >= 0 && nr < MAP_ROWS && nc >= 0 && nc < MAP_COLS && field[nr][nc] > nd) {
          field[nr][nc] = nd;
          queue.push([nr, nc]);
        }
      }
    }
    this.pathDistField = field;
  }

  private distToPath(col: number, row: number): number {
    return this.pathDistField?.[row]?.[col] ?? Infinity;
  }

  /** Pick a tile from candidates, preferring close-to-path but with some spread */
  private pickTile(tiles: PlacementTile[]): PlacementTile | null {
    const available = tiles.filter(t => !this.occupiedTiles.has(`${t.col}_${t.row}`));
    if (available.length === 0) return null;
    // Pick from top 30% closest to path, randomly
    const topN = Math.max(1, Math.floor(available.length * 0.3));
    const idx = Math.floor(Math.random() * topN);
    return available[idx];
  }

  // ══════════════════════════════════════════
  //  LIFECYCLE
  // ══════════════════════════════════════════

  shutdown(): void {
    EventBus.off(GameEvents.PHASE_CHANGED, this.onPhaseChanged, this);
    EventBus.off(GameEvents.WAVE_COMPLETED, this.onWaveCompleted, this);
    EventBus.off(GameEvents.WAVE_TRANSITION_READY, this.onTransitionReady, this);
    if (this.delayTimer) {
      this.delayTimer.destroy();
      this.delayTimer = null;
    }
    this.occupiedTiles.clear();
    this.pathDistField = null;
  }
}
